import React from "react";

import { createTheme, responsiveFontSizes, ThemeProvider as MuiThemeProvider } from "@mui/material";
import PropTypes from "prop-types";

import { useAdvancedThemeUpdates } from "contexts/AdvancedThemeProvider";
import theme from "styles/theme";

import { CustomStyle } from "../components/CustomStyle";

/**
 * The theme provider to use to wrap the widget and the application.
 */
const ThemeProvider = ({
  children,
  constants = null,
  mode = "light",
  themeToMerge = {},
  css = null,
}) => {
  const defaultTheme = constants ? theme(constants, mode) : {};
  const finalTheme = responsiveFontSizes(createTheme({ ...defaultTheme, ...themeToMerge }));

  const widgetBackground = finalTheme?.palette?.customized?.widgetBackground;

  const { css: cssOverride } = useAdvancedThemeUpdates() ?? {};

  React.useEffect(
    () => {
      const rootElement = document.getElementById("weezpay-widget-body");
      if (widgetBackground && rootElement) {
        rootElement.style.backgroundColor = widgetBackground;
      } else if (rootElement) {
        rootElement.style.backgroundColor = null;
      }
    },
    [widgetBackground],
  );

  return (
    <MuiThemeProvider theme={finalTheme}>
      <CustomStyle css={cssOverride === undefined ? css : cssOverride} />
      {children}
    </MuiThemeProvider>
  );
};

ThemeProvider.propTypes = {
  /**
   * The wrapped components that will have access to the final computed theme.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.object]).isRequired,
  /**
   * The object with all the constants to incorporate and use to compute the theme.
   */
  constants: PropTypes.object,
  /**
   * The theme mode to display.
   */
  mode: PropTypes.oneOf(["light", "dark"]),
  /**
   * An optional theme to merge to the computed one.
   */
  themeToMerge: PropTypes.object,
  /**
   * An optional string with custom css to inject in the widget.
   * It will be injected in a style tag at the end of the body, so it will have more priority than the default styles.
   */
  css: PropTypes.string,
};

export default ThemeProvider;
